import React, { useState, useEffect, useCallback } from 'react';
import { Redirect, Tabs } from 'expo-router';
import { Platform, Text, View, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuthStore } from '../../store/authStore';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles } from '../../shared/utils/styles';
import { useResponsive } from '../../shared/utils/responsive';
import { authApi } from '../../shared/utils/api';
import { logger } from '../../shared/utils/logger';

export default function TabsLayout() {
  // ALL HOOKS MUST BE CALLED FIRST - BEFORE ANY CONDITIONAL RETURNS
  const initialized = useAuthStore((state) => state.initialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const user = useAuthStore((state) => state.user);
  const refreshUser = useAuthStore((state) => state.refreshUser);
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const { isMobile, isSmallMobile } = useResponsive();
  
  // Unread discussions count for tab badge
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Responsive tab bar settings using centralized breakpoints
  // isSmallMobile (< 400px): Hide labels, smaller icons
  // isMobile (< 768px): Smaller labels and icons
  const showLabels = !isMobile; // Hide labels on all mobile devices
  const iconSize = isSmallMobile ? 20 : isMobile ? 22 : 24;
  const tabBarHeight = isSmallMobile ? 50 : isMobile ? 56 : 64;

  // Fetch unread discussions count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await authApi.getAllThreads({ limit: 100 });
      const threads = response?.threads || [];
      const totalUnread = threads.reduce((sum, t) => sum + (t.unread_count || 0), 0);
      setUnreadCount(totalUnread);
    } catch (error) {
      // Silently fail - don't disrupt the UI
      logger.debug('Failed to fetch unread count', 'tabs', { error });
    }
  }, []);

  // Enterprise-grade: Refresh user data periodically to ensure role is current
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Initial refresh on mount
    refreshUser();
    fetchUnreadCount();
    
    // Then refresh every 5 minutes for user, every minute for unread count
    const userInterval = setInterval(() => {
      refreshUser();
    }, 5 * 60 * 1000);
    
    const unreadInterval = setInterval(() => {
      fetchUnreadCount();
    }, 60 * 1000); // Check every minute

    return () => {
      clearInterval(userInterval);
      clearInterval(unreadInterval);
    };
  }, [isAuthenticated, refreshUser, fetchUnreadCount]);

  // Debug logging - ALWAYS CALLED (but only logs in dev)
  React.useEffect(() => {
    logger.debug('User auth state', 'auth', {
      userId: user?.id,
      email: user?.email,
      role: user?.role,
      isAdmin: user?.role === 'admin',
    });
  }, [user]);

  // Enterprise-grade: Verify admin role from server data
  const isAdmin = user?.role === 'admin';

  // CONDITIONAL RETURNS AFTER ALL HOOKS
  // Wait for auth initialization to complete before checking auth status
  if (!initialized) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: tokens.color.background }}>
        <ActivityIndicator size="large" color={tokens.color.primary} />
      </View>
    );
  }

  if (!isAuthenticated) {
    return <Redirect href="/(auth)/login" />;
  }

  return (
    <Tabs
      screenOptions={{
        headerShown: false,
        tabBarActiveTintColor: tokens.color.primary,
        tabBarInactiveTintColor: tokens.color.onSurface + '80',
        tabBarShowLabel: showLabels,
        tabBarStyle: {
          backgroundColor: tokens.color.surface,
          borderTopColor: tokens.color.border,
          borderTopWidth: 1,
          height: tabBarHeight,
          paddingBottom: isSmallMobile ? 4 : utils.spacing.sm,
          paddingTop: isSmallMobile ? 4 : utils.spacing.sm,
          paddingHorizontal: 0,
          // Ensure tab bar doesn't overflow
          ...(Platform.OS === 'web' && {
            minWidth: 0,
            overflow: 'hidden' as const,
          }),
        },
        tabBarLabelStyle: showLabels ? {
          fontSize: isMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
          fontWeight: utils.typography.weights.medium,
          fontFamily: utils.typography.body.fontFamily,
          marginTop: -2,
        } : undefined,
        tabBarItemStyle: {
          paddingHorizontal: isSmallMobile ? 2 : isMobile ? 4 : 8,
          minWidth: 0,
          flex: 1,
        },
      }}
    >
      <Tabs.Screen
        name="home"
        options={{
          title: 'Home',
          tabBarLabel: showLabels ? 'Home' : undefined,
          tabBarIcon: ({ color }) => (
            <Ionicons name="home" size={iconSize} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="portfolios"
        options={{
          title: 'Portfolios',
          tabBarLabel: showLabels ? 'Portfolios' : undefined,
          tabBarIcon: ({ color }) => (
            <Ionicons name="folder" size={iconSize} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="contacts"
        options={{
          title: 'Contacts',
          tabBarLabel: showLabels ? 'Contacts' : undefined,
          tabBarIcon: ({ color }) => (
            <Ionicons name="people" size={iconSize} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="discussions"
        options={{
          title: 'Discussions',
          tabBarLabel: showLabels ? 'Discuss' : undefined,
          tabBarIcon: ({ color }) => (
            <View>
              <Ionicons name="chatbubbles" size={iconSize} color={color} />
              {unreadCount > 0 && (
                <View style={{
                  position: 'absolute',
                  top: -4,
                  right: -8,
                  backgroundColor: tokens.color.error,
                  borderRadius: 10,
                  minWidth: 18,
                  height: 18,
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingHorizontal: 4,
                }}>
                  <Text style={{
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: '700',
                  }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Text>
                </View>
              )}
            </View>
          ),
        }}
      />
      <Tabs.Screen
        name="feedback"
        options={{
          title: 'Feedback',
          tabBarLabel: showLabels ? 'Feedback' : undefined,
          tabBarIcon: ({ color }) => (
            <Ionicons name="chatbubble-ellipses" size={iconSize} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="settings"
        options={{
          title: 'Settings',
          tabBarLabel: showLabels ? 'Settings' : undefined,
          tabBarIcon: ({ color }) => (
            <Ionicons name="person-circle" size={iconSize} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="admin"
        options={{
          title: 'Admin',
          tabBarLabel: showLabels ? 'Admin' : undefined,
          tabBarIcon: ({ color }) => (
            <Ionicons name="shield-checkmark" size={iconSize} color={color} />
          ),
          // Hide tab if not admin, but keep the route accessible
          tabBarButton: isAdmin ? undefined : () => null,
        }}
      />
    </Tabs>
  );
}

